import type {
  ClientStatus,
  ProjectStatus,
  WebsiteEnvironment,
  WebsiteStatus,
} from "../workspace/field-map.js";

export type { WebsiteEnvironment };

export interface ApiErrorBody {
  readonly error?: string;
  readonly message?: string;
}

export interface LoginResponse {
  readonly accessToken: string;
  readonly sessionId: string;
  readonly expiresInSeconds: number;
}

export interface OrganizationSummary {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
}

export interface ClientSummary {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly status: ClientStatus;
  readonly email: string | null;
  readonly phone: string | null;
  readonly updatedAt: string;
  readonly version: number;
}

export interface ClientDetail extends ClientSummary {
  readonly taxIdentifier: string | null;
  readonly taxCountryCode: string | null;
  readonly addressLine1: string | null;
  readonly addressLine2: string | null;
  readonly postalCode: string | null;
  readonly city: string | null;
  readonly region: string | null;
  readonly countryCode: string | null;
  readonly internalNotes?: string | null;
  readonly createdAt: string;
  readonly archivedAt: string | null;
}

/** Required fields for POST create Client — matches existing API contract. */
export interface CreateClientInput {
  readonly name: string;
  readonly slug: string;
}

export interface CreateClientResult {
  readonly id: string;
  readonly version: number;
}

/** Minimal PATCH Client fields — matches existing API contract (name + expectedVersion). */
export interface UpdateClientInput {
  readonly clientId: string;
  readonly name: string;
  readonly expectedVersion: number;
}

export interface UpdateClientResult {
  readonly id: string;
  readonly version: number;
  readonly status: "ACTIVE" | "ARCHIVED";
}

export interface ArchiveClientInput {
  readonly clientId: string;
  readonly expectedVersion: number;
}

export interface RestoreClientInput {
  readonly clientId: string;
  readonly expectedVersion: number;
}

/** Required fields for POST create Website — matches existing API contract. */
export interface CreateWebsiteInput {
  readonly clientId: string;
  readonly name: string;
  readonly domain: string;
  readonly ownerMembershipId: string;
  readonly environment: WebsiteEnvironment;
}

export interface CreateWebsiteResult {
  readonly id: string;
  readonly version: number;
}

/** Minimal PATCH Website fields — matches existing API contract. */
export interface UpdateWebsiteInput {
  readonly websiteId: string;
  readonly name: string;
  readonly domain: string;
  readonly expectedVersion: number;
}

export interface UpdateWebsiteResult {
  readonly id: string;
  readonly version: number;
  readonly status: string;
}

export interface ArchiveWebsiteInput {
  readonly websiteId: string;
  readonly expectedVersion: number;
}

export interface RestoreWebsiteInput {
  readonly websiteId: string;
  readonly expectedVersion: number;
}

/** Required fields for POST create Project — matches existing API contract. */
export interface CreateProjectInput {
  readonly clientId: string;
  readonly name: string;
  readonly ownerMembershipId: string;
}

export interface CreateProjectResult {
  readonly id: string;
  readonly version: number;
}

/** Minimal PATCH Project fields — matches existing API contract. */
export interface UpdateProjectInput {
  readonly projectId: string;
  readonly name: string;
  readonly expectedVersion: number;
}

export interface UpdateProjectResult {
  readonly id: string;
  readonly version: number;
  readonly status: string;
}

export interface ArchiveProjectInput {
  readonly projectId: string;
  readonly expectedVersion: number;
}

export interface RestoreProjectInput {
  readonly projectId: string;
  readonly expectedVersion: number;
}

export interface LinkWebsiteToProjectInput {
  readonly websiteId: string;
  readonly projectId: string;
  readonly expectedVersion: number;
}

export interface LinkWebsiteToProjectResult {
  readonly id: string;
  readonly version: number;
  readonly status: string;
}

export interface WebsiteSummary {
  readonly id: string;
  readonly organizationId: string;
  readonly clientId: string;
  readonly name: string;
  readonly domain: string;
  readonly status: WebsiteStatus;
  readonly technology: string | null;
  /** Frozen Website contract — required when Primary Website known (RFC v0.5.3 §2.1). */
  readonly environment: WebsiteEnvironment;
  readonly ownerMembershipId: string;
  readonly projectId: string | null;
  readonly archivedAt: string | null;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface WebsiteDetail extends WebsiteSummary {
  readonly notes: string | null;
}

export interface ProjectSummary {
  readonly id: string;
  readonly organizationId: string;
  readonly clientId: string;
  readonly name: string;
  readonly status: ProjectStatus;
  readonly ownerMembershipId: string;
  readonly plannedCompletion: string | null;
  readonly archivedAt: string | null;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ProjectDetail extends ProjectSummary {
  readonly description: string | null;
  readonly openAttentions: readonly ProjectAttentionDetail[];
  readonly resolvedAttentions: readonly ProjectAttentionDetail[];
}

export interface ProjectAttentionDetail {
  readonly id: string;
  readonly projectId: string;
  readonly title: string;
  readonly status: "OPEN" | "RESOLVED";
  readonly source: "CUSTOMER_REPORTED" | "OPERATOR_RECORDED";
  readonly description: string;
  readonly customerQuote: string;
  readonly suggestedAction: string | null;
  readonly createdAt: string;
  readonly resolvedAt: string | null;
}

/** Required fields for POST create Project Attention — matches existing API contract. */
export interface CreateAttentionInput {
  readonly projectId: string;
  readonly title: string;
  readonly description: string;
  readonly source: ProjectAttentionDetail["source"];
}

export type CreateAttentionResult = ProjectAttentionDetail;

export interface ResolveAttentionInput {
  readonly projectId: string;
  readonly attentionId: string;
}

export type ResolveAttentionResult = ProjectAttentionDetail;

export interface MembershipSummary {
  readonly id: string;
  readonly displayName: string;
  readonly userId: string;
  readonly email?: string;
  readonly status?: "ACTIVE" | "SUSPENDED" | "REMOVED";
  readonly roleIds?: readonly string[];
  readonly version?: number;
}

export interface RoleSummary {
  readonly id: string;
  readonly key: string;
  readonly name: string;
  readonly description: string | null;
  readonly isSystem: boolean;
  readonly version: number;
}

export interface InvitationSummary {
  readonly id: string;
  readonly email: string;
  readonly roleId: string;
  readonly status: "PENDING" | "ACCEPTED" | "DECLINED" | "REVOKED" | "EXPIRED";
  readonly expiresAt: string;
  readonly version: number;
}

/** POST invite — matches existing API contract (display fields ignored for authority). */
export interface InviteMemberInput {
  readonly email: string;
  readonly roleId: string;
  readonly organizationName: string;
  readonly inviterName: string;
}

export interface InviteMemberResult {
  readonly delivery: "SENT" | "FAILED";
  readonly errorId?: string;
}

export interface RegisterUserInput {
  readonly email: string;
  readonly password: string;
  readonly displayName: string;
}

export interface RegisterUserResult {
  readonly userId: string;
}

export interface AcceptInvitationResult {
  readonly organizationId: string;
}

export interface Page<T> {
  readonly items: readonly T[];
  readonly nextCursor: string | null;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
  }
}
